import { useState, useEffect, useRef } from "react"
import { ReactSVG } from "react-svg"
import find from "lodash/find"
import noop from "lodash/noop"
import Typography from "component-lib/Base/components/Typography"
import getContentUrl from "utils-lib/getContentUrl"
import InputAdornment from "component-lib/Base/components/InputAdornment"
import TextField from "component-lib/Base/components/TextField"
import { Box } from "component-lib/Base/components/Box"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { Index, Configure, useSearchBox } from "react-instantsearch"
import { makeStyles } from "tss-react/mui"
import { Button } from "component-lib/Base"
import { KEY_CODE_MAPPING } from "constants-lib/keyCodes"
import { BOOTSTRAP_GRID_MD_MAX } from "constants-lib/dimensions"
import { getWindow } from "utils-lib/getWindow"
import closest from "utils-lib/closest"
import { useAutocomplete } from "../utils/useAutocomplete"
import Results from "../components/Results/Results"

const ssrWindow = getWindow()

let timer

const useStyles = makeStyles()((theme, { isGreenBG, isFocused }) => ({
  inputContainer: {
    minHeight: `3rem`,
    height: `3.125rem`,
    width: `100%`,
    [theme.breakpoints.up("lg")]: {
      width: 828,
    },
  },
  input: {
    height: `inherit`,
    background: `#fff`,
    border: `1px solid #EFEFEF`,
    borderRadius: 10,
    boxShadow: `0 1px 4px 0 rgba(0,0,0,0.2)`,
    margin: 0,
    marginTop: `.5rem`,
    "& .MuiInputBase-input.MuiOutlinedInput-input": {
      padding: `0 !important`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `none`,
      padding: `0 !important`,
    },
    "&:hover": {
      border: isFocused ? `1px solid #EFEFEF` : `1px solid #1D890A`,
    },
    "&::before": isFocused
      ? {
          content: '""',
          display: "block",
          background: `#fff`,
          height: 2,
          width: `calc(100% - 2px)`,
          position: `absolute`,
          bottom: 7,
          left: 0,
          zIndex: 98,
          marginLeft: 1,
        }
      : {},
  },
  searchIcon: {
    width: `1.6rem`,
    minHeight: `2rem`,
  },
  svg: {
    "& > svg": {
      height: 25,
      width: 25,
    },
    "& ellipse, line": {
      stroke: `#1D890A`,
    },
  },
  allResults: {
    border: `1px solid #D2E7CE !important`,
    borderRadius: `5px !important`,
  },
  inputUnderline: {
    background: theme.palette.background.white,
    height: 2,
    top: -12,
    width: `calc(100% - 32px)`,
  },
  dropdown: {
    left: 0,
    top: 51,
    maxHeight: 560,
    overflowY: `auto`,
    border: `1px solid #EFEFEF`,
    background: theme.palette.background.white,
    boxShadow: `0 1px 4px 0 rgba(0,0,0,0.2)`,
    borderTop: 0,
    borderRadius: `0 0 10px 10px`,
    transition: `.3s all`,
    zIndex: 1,
  },
  headerTitle: {
    lineHeight: `50px`,
    fontWeight: `400`,
    letterSpacing: `normal`,
    color: isGreenBG
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main2_0,
  },
  esc: {
    top: -6,
  },
  closeIcon: {
    top: 0,
    right: -50,
    [theme.breakpoints.down("md")]: {
      right: 0,
    },
  },
}))

export const SupportSearch = ({
  isDisplayOnly = false,
  header,
  indices,
  refine = noop,
  numResults = 0,
  numCharacters = 0,
  errorMessageTitle = "",
  errorMessageDescription = "",
  failureTitle = "",
  failureDescription = "",
  error = {},
  setShowMobileSearch = noop,
  suggestedText = "",
  searchPlaceholder,
  supportIndex,
  setIsFocused,
  searchDelay,
  headingTag,
  componentAlignment,
  headingAlignment,
  isGreenBG = false,
}) => {
  const [search, setSearch] = useState(``)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchContainerRef = useRef()
  const { classes } = useStyles({
    isGreenBG,
    isFocused: !isDisplayOnly,
  })

  const hits =
    find(indices, ({ indexId }) => indexId === supportIndex)?.hits || []

  useEffect(() => {
    if (!isDisplayOnly) {
      const inputEl = ssrWindow.document.getElementById(
        "full-width-text-field-header",
      )

      if (inputEl) {
        inputEl.focus()
      }
    }

    ssrWindow.document.addEventListener("keydown", handleEsc)

    ssrWindow.document.addEventListener("click", handleSearchClick)

    return () => {
      ssrWindow.document.removeEventListener("keydown", handleEsc)

      ssrWindow.document.removeEventListener("click", handleSearchClick)
    }
  }, [])
  const searchResultElements = searchContainerRef.current?.children
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (searchResultElements?.length > 0) {
      const resultsElementsArr = Array.from(searchResultElements)
      const lastElementIndex = resultsElementsArr.length - 1
      if (lastElementIndex > 0) {
        const lastSearchElement = resultsElementsArr[lastElementIndex]
        lastSearchElement.addEventListener("focusout", closeSelect)
        return () =>
          lastSearchElement.removeEventListener("focusout", closeSelect)
      }
    }
  }, [searchResultElements?.length])
  const closeSelect = () => {
    setShowDropdown(false)
    setIsFocused(false)
  }

  useEffect(() => {
    if (showDropdown) {
      ssrWindow.document.body.style.overflow = `hidden`
    } else {
      ssrWindow.document.body.style.overflow = `auto`
    }
  }, [showDropdown])

  const slicedHits = hits.slice(0, numResults)

  function handleSearchClick(e) {
    if (
      !closest(e.target, `.MuiFormControl-root`) &&
      !closest(e.target, `.CustomAutoCompleteSupport__dropdown`)
    ) {
      setShowDropdown(false)
    }
  }

  function handleEsc(e) {
    if (e.keyCode === KEY_CODE_MAPPING.ESCAPE) {
      setShowDropdown(false)
      ssrWindow.document.activeElement.blur()
    }
  }

  function renderResults() {
    return (
      <Index indexName={supportIndex} indexId={`${supportIndex}-results`}>
        <Configure
          analyticsTags={["support-results"]}
          hitsPerPage={numResults}
          clickAnalytics
          filters="searchType:support"
        />
        <Results
          hideDescription
          hideKeywords
          isHeader={false}
          indexName={`${supportIndex}-results`}
        />
      </Index>
    )
  }

  function renderDropdown() {
    return (
      <div className="CustomAutoCompleteSupport__dropdown">
        {!isDisplayOnly && (
          <div className={`mx-3 position-relative ${classes.inputUnderline}`} />
        )}

        <div
          className={`position-absolute w-100 maax-font-weight-regular ${classes.dropdown}`}
          style={{
            maxHeight: showDropdown ? 560 : 0,
            borderWidth: showDropdown ? 1 : 0,
          }}
        >
          <div className="pb-3">
            <div
              className="font-xs py-1 px-2"
              style={{
                background: `#f4f4f4`,
              }}
            >
              {suggestedText}
            </div>
            {slicedHits.length === 0 && search && (
              <div className="mt-3 mx-3">
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className="mx-3 wm-grey-light"
                >
                  <div className="maax-font-weight-bold">
                    {error && error.name ? failureTitle : errorMessageTitle}
                  </div>
                  <div className="font-sm">
                    {error && error.name
                      ? failureDescription
                      : errorMessageDescription}
                  </div>
                </div>
                <div
                  role="status"
                  aria-live="polite"
                  id="statusMessage"
                  className="sr-only"
                >
                  {error && error.name
                    ? `${failureTitle} ${failureDescription}`
                    : `${errorMessageTitle} ${errorMessageDescription}`}
                </div>
              </div>
            )}
            <div className="px-3" ref={searchContainerRef}>
              {renderResults()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div data-testid="Autocomplete">
      <TransitionGroup className="todo-list">
        {showDropdown && (
          <CSSTransition timeout={300} classNames="SiteSearch__overlay">
            <div
              className="position-absolute"
              style={{
                background: `rgba(0, 0, 0, .5)`,
                top: 184,
                left: 0,
                height: `100vh`,
                width: `100%`,
                zIndex: 1,
              }}
            />
          </CSSTransition>
        )}
      </TransitionGroup>
      <div>
        <div className="container mb-2">
          <Box
            display="flex"
            flexDirection="column"
            alignItems={componentAlignment}
          >
            <Box textAlign={headingAlignment}>
              <Typography
                className={`mb-3 d-none d-lg-flex ${classes.headerTitle}`}
                key={header}
                variant={headingTag}
                component={headingTag}
              >
                {header}
              </Typography>
            </Box>
            <div className={`position-relative ${classes.inputContainer}`}>
              <TextField
                data-testid="inputbox"
                id="full-width-text-field-header"
                analytics="support-search-query-result-selection"
                className={`SitieSearch__searchbox-support a-searchTaggable a py-2 px-3 ${classes.input}`}
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => {
                  const { value } = e.target

                  setSearch(value)

                  const supportAnalytics = ssrWindow._dl.supportDataLayer9_0
                  if (!supportAnalytics) {
                    ssrWindow._dl.supportDataLayer9_0 = {
                      supportPageSearchResults: value,
                    }
                  } else {
                    ssrWindow._dl.supportDataLayer9_0.supportPageSearchResults =
                      value
                  }

                  if (value.length >= numCharacters || !value.length) {
                    clearTimeout(timer)
                    timer = setTimeout(() => refine(value), searchDelay)
                  }
                }}
                onFocus={() => {
                  if (
                    ssrWindow.document.body.offsetWidth <= BOOTSTRAP_GRID_MD_MAX
                  ) {
                    setShowMobileSearch(true, true)
                    ssrWindow.document.activeElement.blur()
                  } else {
                    setShowDropdown(true)
                    setIsFocused(true)
                  }
                }}
                onBlur={() => {
                  setIsFocused(true)
                }}
                fullWidth
                margin="normal"
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={`${classes.searchIcon}`}
                    >
                      <ReactSVG
                        wrapper="span"
                        className={classes.svg}
                        src={getContentUrl(
                          "/content/dam/wm/icons/search-icon.svg",
                        )}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <div
                className={`d-none ${
                  showDropdown ? "d-sm-flex" : ""
                } mt-4 mr-3 flex-column align-items-end position-absolute wm-cursor-pointer ${
                  classes.closeIcon
                }`}
              >
                <ReactSVG
                  wrapper="span"
                  className="flex-shrink-0"
                  src={getContentUrl(
                    "/content/dam/mywm/icons/green-close-x-icon.svg",
                  )}
                />
                <div className={`position-relative wm-link ${classes.esc}`}>
                  Esc
                </div>
              </div>
              <Button
                onClick={() => {
                  setShowDropdown(false)
                }}
                analytics="global_search_searchbox_exit_esc_click"
                overrideClass={`d-sm-none ${
                  showDropdown ? "d-block" : "d-none"
                } mt-4 mr-3 d-flex flex-column align-items-end position-absolute a-searchTaggable ${
                  classes.closeIcon
                }`}
                fontClass="maax-font-weight-regular"
              >
                <ReactSVG
                  wrapper="span"
                  className="flex-shrink-0"
                  src={getContentUrl(
                    "/content/dam/mywm/icons/green-close-x-icon.svg",
                  )}
                />
              </Button>
              {!isDisplayOnly && renderDropdown()}
            </div>
          </Box>
        </div>
      </div>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// SupportSearch.propTypes = {
//   header: PropTypes.string.isRequired,
//   indices: PropTypes.array.isRequired,
//   refine: PropTypes.string.isRequired,
//   error: PropTypes.object,
//   errorMessageTitle: PropTypes.string.isRequired,
//   errorMessageDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   headingTag: PropTypes.string.isRequired,
//   componentAlignment: PropTypes.string.isRequired,
//   headingAlignment: PropTypes.string.isRequired,
//   setShowMobileSearch: PropTypes.func,
//   searchPlaceholder: PropTypes.string.isRequired,
//   supportIndex: PropTypes.string.isRequired,
//   setIsFocused: PropTypes.func.isRequired,
//   numResults: PropTypes.number,
//   numCharacters: PropTypes.number,
//   isDisplayOnly: PropTypes.bool,
//   isGreenBG: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// SupportSearch.defaultProps = {
//   setShowMobileSearch: noop,
//   numResults: 0,
//   numCharacters: 0,
//   isDisplayOnly: false,
//   isGreenBG: false,
//   error: {},
// }

const SupportSearchWithContainer = (props) => {
  const stateResults = useSearchBox(props)
  const autoCompleteResults = useAutocomplete({ ...props, ...stateResults })

  return <SupportSearch {...props} {...stateResults} {...autoCompleteResults} />
}

export default SupportSearchWithContainer
